import { FC, lazy, Suspense, useContext, useState } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router'

import { MoonOutlined, SunOutlined } from '@ant-design/icons'
import { Button, Col, Flex, Modal, Row, Select, Spin, theme } from 'antd'
import { useResponsive } from 'antd-style'

import { Context } from '../../providers/Config'
import { Context as IntlContext } from '../../providers/Intl'
import { Context as SeoContext } from '../../providers/Seo'
import routes from '../../routes'
import { Country } from '../icon/Country'
import { Icon } from '../icon/Icon'
import { Logo } from '../logo/Logo'

import { footerItems } from './index'

const { useToken } = theme

const Contact = lazy(() => import('../../components/user/Contact'))

const ThemeIcon: FC<{ theme: 'dark' | 'light' }> = ({ theme }) => {
  const { token } = useToken()

  return theme === 'dark' ? (
    <SunOutlined style={{ color: token.colorWhite }} />
  ) : (
    <MoonOutlined style={{ color: token.colorPrimary }} />
  )
}

const ThemeSelect: FC = () => {
  const { theme, setTheme } = useContext(Context)

  return (
    <Button
      type={'link'}
      shape={'circle'}
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      icon={<ThemeIcon theme={theme} />}
    />
  )
}

const SizeSelect: FC = () => {
  const { setSize } = useContext(Context)

  return (
    <Flex gap={4} align={'center'} hidden={true}>
      <Button
        type={'link'}
        shape={'circle'}
        onClick={() => setSize('small')}
        icon={
          <span>
            A<sup>-</sup>
          </span>
        }
      />
      <Button type={'link'} shape={'circle'} onClick={() => setSize('default')} icon={<span>A</span>} />
      <Button
        type={'link'}
        shape={'circle'}
        onClick={() => setSize('large')}
        icon={
          <span>
            A<sup>+</sup>
          </span>
        }
      />
    </Flex>
  )
}

const LanguageSelect: FC = () => {
  const { locale, setLocale } = useContext(IntlContext)

  return (
    <Select
      suffixIcon={null}
      onChange={setLocale}
      variant={'borderless'}
      popupMatchSelectWidth={false}
      value={locale}
      options={[
        {
          label: (
            <Flex align={'center'} gap={8}>
              <Country height={16} country={'US'} />
            </Flex>
          ),
          value: 'en',
          title: 'English',
        },
        {
          label: (
            <Flex align={'center'} gap={8}>
              <Country height={16} country={'UA'} />
            </Flex>
          ),
          value: 'uk',
          title: 'Українська',
        },
      ]}
    />
  )
}
const Socials: FC = () => {
  const { socials } = useContext(SeoContext)

  return (
    <Flex gap={16}>
      {socials?.map(
        (it) =>
          it && (
            <Link rel={'nofollow noreferrer'} to={it.url} target={'_blank'} key={it.id}>
              <Icon type={it.type} />
            </Link>
          ),
      )}
    </Flex>
  )
}

const FooterLinks: FC = () => {
  const { formatMessage } = useIntl()
  const [open, setOpen] = useState(false)
  return (
    <Flex gap={16}>
      {footerItems.map(({ Icon, key, label }) => {
        return (
          <Link
            to={key !== routes.contact ? key : '#'}
            key={key}
            onClick={key === routes.contact ? () => setOpen(true) : undefined}
          >
            <Flex gap={8} style={{ fontSize: 14 }}>
              <Icon />
              {formatMessage({ id: label })}
            </Flex>
          </Link>
        )
      })}
      <Modal
        title={formatMessage({ id: 'contact.title' })}
        okButtonProps={{ hidden: true }}
        cancelButtonProps={{ hidden: true }}
        open={open}
        onCancel={() => setOpen(false)}
      >
        <Suspense fallback={<Spin spinning size={'small'} />}>
          <Contact />
        </Suspense>
      </Modal>
    </Flex>
  )
}

const Footer: FC = () => {
  const { mobile } = useResponsive()
  return (
    <Row style={{ width: '100%' }} align={'middle'} justify={'space-between'} gutter={[16, 16]}>
      <Col xs={{ span: 12, order: 1, flex: 'auto' }} lg={{ flex: 'auto' }}>
        <Flex align={'center'} gap={16}>
          <Logo size={24} />
          <LanguageSelect />
          <SizeSelect />
          <ThemeSelect />
          {mobile ? null : (
            <Flex gap={16}>
              <FooterLinks />
            </Flex>
          )}
        </Flex>
      </Col>
      <Col xs={{ span: 12, order: 1, flex: 'none' }} style={{ textAlign: 'right' }} lg={{ flex: 'none' }}>
        <div style={{ position: 'relative', left: 0 }}>
          <Socials />
        </div>
      </Col>
    </Row>
  )
}

export { Footer }
